/**
 * AuthLayout — centered card layout for auth pages (login, register).
 * Deep-space visual language with brand gradient accents.
 *
 * @module apps/web/components/AuthLayout
 * @since W2.9
 */

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-deep-space">
      {/* Ambient glow blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-brand-indigo/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-brand-cyan/8 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md animate-slide-up">
        {/* Logo / wordmark */}
        <div className="mb-8 text-center">
          <span className="text-3xl font-bold brand-gradient-text tracking-tight">rule42</span>
          <p className="mt-1 text-xs text-slate-500 font-mono tracking-widest uppercase">
            AI-Powered Methodology Platform
          </p>
        </div>

        {/* Card */}
        <div className="card-glass p-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-100">{title}</h1>
            {subtitle && <p className="mt-1.5 text-sm text-slate-400">{subtitle}</p>}
          </div>

          {children}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-600">
          &copy; {new Date().getFullYear()} rule42. All rights reserved.
        </p>
      </div>
    </div>
  );
}
